// Small hash router. Hash URLs (#/home) work everywhere: static hosting, Capacitor's file://
// and the single-file preview. Swap for TanStack Router later if needed; routes are declared in App.tsx.
import { useEffect, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';

function current(): string {
  const h = window.location.hash.replace(/^#/, '');
  return h.startsWith('/') ? h : '/';
}

function subscribe(l: () => void) {
  window.addEventListener('hashchange', l);
  return () => window.removeEventListener('hashchange', l);
}

export function useLocation(): { path: string; query: URLSearchParams } {
  const full = useSyncExternalStore(subscribe, current, () => '/');
  const [path, qs = ''] = full.split('?');
  return { path, query: new URLSearchParams(qs) };
}

type DocWithVT = Document & { startViewTransition?: (cb: () => Promise<void> | void) => unknown };

function canTransition(): boolean {
  try {
    return !!(document as DocWithVT).startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
}

/** opts.transition: morph between pages with the View Transitions API where supported (e.g. a poster growing into the show header). */
export function navigate(to: string, opts: { replace?: boolean; transition?: boolean } = {}): void {
  const url = '#' + to;
  const go = () => {
    if (opts.replace) {
      window.history.replaceState(null, '', url);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } else {
      window.location.hash = to;
    }
  };
  if (opts.transition && canTransition()) {
    (document as DocWithVT).startViewTransition!(() => new Promise<void>((done) => {
      go();
      requestAnimationFrame(() => done());
    }));
  } else go();
}

export function back(fallback = '/home'): void {
  if (window.history.length > 1) window.history.back();
  else navigate(fallback);
}

/** Match "/show/:slug" against "/show/pan-card" -> { slug: "pan-card" } */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('/').filter(Boolean);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(a[i]);
    else if (p[i] !== a[i]) return null;
  }
  return params;
}

export function Link({ to, onClick, transition, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; transition?: boolean }) {
  return (
    <a
      href={'#' + to}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (transition && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && canTransition()) {
          e.preventDefault();
          navigate(to, { transition: true });
        }
      }}
      {...rest}
    />
  );
}

export function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}

/** Scroll to top on route change (except within the reels feed). */
export function useScrollReset(path: string) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);
}
